import React, { useEffect } from "react";
import NotificationFilters from "./NotificationFilters";
import StatusFilterMessage from "./list/StatusFilterMessage";
import BulkActionsToolbar from "./BulkActionsToolbar";
import NotificationTable from "./NotificationTable";
import { useNotificationBulkSelection } from "@/hooks/notifications/useNotificationBulkSelection";
import { useNotificationBulkOperations } from "@/hooks/notifications/useNotificationBulkOperations";
import { useNotificationFilters } from "@/hooks/notifications/useNotificationFilters";
import { usePermissions } from "@/context/PermissionsContext";
import { getStatusDisplayLabel } from "./utils/statusMapping";

interface NotificationListProps {
  notifications: any[];
  filteredNotifications: any[];
  sortedNotifications: any[];
  isLoading: boolean;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  sortConfig?: { key: string; direction: 'asc' | 'desc' };
  requestSort: (key: string) => void;
  formatDate: (date: string) => string;
  handleNotificationClick: (id: string) => void;
  handleArchive: (e: React.MouseEvent, id: string) => void;
  handleRestore: (e: React.MouseEvent, id: string) => void;
  handleDelete: (e: React.MouseEvent, id: string) => void;
  handleSendWebhook?: (e: React.MouseEvent, id: string) => void;
  viewMode: "active" | "archived";
  unreadCount?: number;
  getUnreadChatCount?: (notificationId: string) => number;
}

const NotificationList = ({
  notifications,
  filteredNotifications,
  sortedNotifications,
  isLoading,
  searchTerm,
  setSearchTerm,
  sortConfig,
  requestSort,
  formatDate,
  handleNotificationClick,
  handleArchive,
  handleRestore,
  handleDelete,
  handleSendWebhook,
  viewMode,
  unreadCount = 0,
  getUnreadChatCount
}: NotificationListProps) => {
  const { hasPermission } = usePermissions();
  
  // Use notification filters hook
  const {
    unreadFilter,
    selectedStatuses,
    filteredUnreadCount,
    displayedNotifications,
    handleStatusChange,
    toggleUnreadFilter
  } = useNotificationFilters({
    sortedNotifications,
    unreadCount
  });

  // Bulk selection management
  const {
    selectedIds,
    selectedCount,
    selectedNotifications,
    isAllSelected,
    isPartiallySelected,
    toggleSelectAll,
    toggleSelectItem,
    clearSelection,
    isSelected
  } = useNotificationBulkSelection(displayedNotifications);

  // Bulk operations
  const {
    bulkMarkAsRead,
    bulkMarkAsUnread,
    bulkArchive,
    bulkRestore,
    bulkDelete,
    bulkChangeStatus
  } = useNotificationBulkOperations();

  // Clear selection when switching modes or filters
  useEffect(() => {
    clearSelection();
  }, [viewMode, JSON.stringify(selectedStatuses), unreadFilter, searchTerm, clearSelection]);

  // Bulk operation handlers
  const handleBulkMarkAsRead = () => {
    if (!hasPermission("notifications", "write")) return;
    bulkMarkAsRead.mutate(Array.from(selectedIds));
    clearSelection();
  };

  const handleBulkMarkAsUnread = () => {
    if (!hasPermission("notifications", "write")) return;
    bulkMarkAsUnread.mutate(Array.from(selectedIds));
    clearSelection();
  };

  const handleBulkArchive = () => {
    if (!hasPermission("notifications", "write")) return;
    bulkArchive.mutate(Array.from(selectedIds));
    clearSelection();
  };

  const handleBulkRestore = () => {
    if (!hasPermission("notifications", "write")) return;
    bulkRestore.mutate(Array.from(selectedIds));
    clearSelection();
  };

  const handleBulkDelete = () => {
    if (!hasPermission("notifications", "delete")) return;
    bulkDelete.mutate(Array.from(selectedIds));
    clearSelection();
  };

  const handleBulkChangeStatus = (newStatus: string) => {
    if (!hasPermission("notifications", "write")) return;
    bulkChangeStatus.mutate({ notificationIds: Array.from(selectedIds), newStatus });
    clearSelection();
  };

  const canUseBulkActions = hasPermission("notifications", "write") || hasPermission("notifications", "delete");
  
  return (
    <div className="flex flex-col space-y-4">
      <NotificationFilters 
        searchTerm={searchTerm} 
        setSearchTerm={setSearchTerm} 
        unreadFilter={unreadFilter}
        toggleUnreadFilter={toggleUnreadFilter}
        unreadCount={filteredUnreadCount}
        selectedStatuses={selectedStatuses}
        onStatusChange={handleStatusChange}
      />

      {canUseBulkActions && selectedCount > 0 && (
        <BulkActionsToolbar
          selectedCount={selectedCount}
          selectedNotifications={selectedNotifications}
          viewMode={viewMode}
          onMarkAsRead={handleBulkMarkAsRead}
          onMarkAsUnread={handleBulkMarkAsUnread}
          onArchive={handleBulkArchive}
          onRestore={handleBulkRestore}
          onDelete={handleBulkDelete}
          onChangeStatus={handleBulkChangeStatus}
          onClearSelection={clearSelection}
          isLoading={bulkMarkAsRead.isPending || bulkMarkAsUnread.isPending || bulkArchive.isPending || bulkRestore.isPending || bulkDelete.isPending || bulkChangeStatus.isPending}
        />
      )}

      <NotificationTable
        isLoading={isLoading}
        notifications={notifications}
        displayedNotifications={displayedNotifications}
        viewMode={viewMode}
        formatDate={formatDate}
        sortConfig={sortConfig}
        requestSort={requestSort}
        handleNotificationClick={handleNotificationClick}
        handleArchive={handleArchive}
        handleRestore={handleRestore}
        handleDelete={handleDelete}
        handleSendWebhook={handleSendWebhook}
        canUseBulkActions={canUseBulkActions}
        isAllSelected={isAllSelected}
        isPartiallySelected={isPartiallySelected}
        toggleSelectAll={toggleSelectAll}
        isSelected={isSelected}
        toggleSelectItem={toggleSelectItem}
        getUnreadChatCount={getUnreadChatCount}
      />
      
      {selectedStatuses.length > 0 && (
        <div className="text-sm text-muted-foreground italic">
          {selectedStatuses.length === 1 
            ? `Filtering op status: ${getStatusDisplayLabel(selectedStatuses[0])}`
            : `Filtering op ${selectedStatuses.length} statussen`
          }
        </div>
      )}
    </div>
  );
};

export default NotificationList;
